import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "@/lib/toast";
import { CreditCard, Loader2, Trash2 } from "lucide-react";
import { useState } from "react";

import { Card, CardContent, CardHeader } from "@/ui-components/ui/card";
import { SectionHeader } from "@/util-components/page-layout";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { Button } from "@/ui-components/ui/button";
import { Checkbox } from "@/ui-components/ui/checkbox";
import { Textarea } from "@/ui-components/ui/textarea";
import { Label } from "@/ui-components/ui/label";

import {
  useOrganizationsInternalGetBillingContractQuery,
  useOrganizationsInternalUpdateBillingContract,
  useOrganizationsInternalDeleteBillingContract,
} from "@/lib/api/requests/internal.requests";
import { BillingStatus, InternalPlanKey, BillingSource } from "@/lib/api/interfaces/internal.interface";
import { useFormConflictError } from "@/lib/hooks/use-form-conflict-error";
import { StandardDeleteDialog } from "@/util-components/dialog/StandardDeleteDialog";

// Schema
const subscriptionSchema = z.object({
  stripeCustomerId: z.string().optional(),
  stripeSubscriptionId: z.string().optional(),
  stripePriceId: z.string().optional(),
  planKey: z.nativeEnum(InternalPlanKey),
  status: z.nativeEnum(BillingStatus),
  source: z.nativeEnum(BillingSource),
  isPlanOverridden: z.boolean().default(false),
  planOverrideReason: z.string().optional(),
});

type SubscriptionSchema = z.infer<typeof subscriptionSchema>;

export const SubscriptionDetailsCard = () => {
  const { id } = useParams<{ id: string }>();
  const { data: contract, isLoading } = useOrganizationsInternalGetBillingContractQuery(id!);
  const { mutate: updateContract, isPending: isUpdating } = useOrganizationsInternalUpdateBillingContract();
  const { mutateAsync: deleteContract, isPending: isDeleting } = useOrganizationsInternalDeleteBillingContract();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [shouldCancelStripe, setShouldCancelStripe] = useState(false);

  const form = useForm<SubscriptionSchema>({
    resolver: zodResolver(subscriptionSchema),
    defaultValues: {
      stripeCustomerId: contract?.stripeCustomerId || "",
      stripeSubscriptionId: contract?.stripeSubscriptionId || "",
      stripePriceId: contract?.stripePriceId || "",
      planKey: contract?.planKey || InternalPlanKey.BASIC,
      status: contract?.status || BillingStatus.INACTIVE,
      source: contract?.source || BillingSource.MANUAL,
      isPlanOverridden: contract?.isPlanOverridden || false,
      planOverrideReason: contract?.planOverrideReason || "",
    },
    values: contract ? {
        stripeCustomerId: contract.stripeCustomerId || "",
        stripeSubscriptionId: contract.stripeSubscriptionId || "",
        stripePriceId: contract.stripePriceId || "",
        planKey: contract.planKey,
        status: contract.status,
        source: contract.source,
        isPlanOverridden: contract.isPlanOverridden || false,
        planOverrideReason: contract.planOverrideReason || "",
    } : undefined
  });

  const { handleConflictError } = useFormConflictError(form);

  const onSubmit = (data: SubscriptionSchema) => {
    updateContract(
      {
        id: id!,
        data: {
          ...data,
          // Only send reason if overridden
          planOverrideReason: data.isPlanOverridden ? data.planOverrideReason : undefined,
        },
        requestOptions: {
          skipGlobalErrorHandler: true,
          throwOnError: true
        }
      },
      {
        onSuccess: () => {
          toast.success("Abonnement-Details aktualisiert");
        },
        onError: (error: any) => {
             handleConflictError(error);
        },
      }
    );
  };

  const handleDeleteContract = async () => {
    try {
      await deleteContract({
        id: id!,
        cancelStripeSubscription: shouldCancelStripe,
      });
      toast.success("Vertrag erfolgreich gelöscht");
      setIsDeleteDialogOpen(false);
      // Optional: Refresh or redirect
    } catch (error) {
      toast.error("Vertrag konnte nicht gelöscht werden", { error });
    }
  };

  if (isLoading) {
      return (
          <Card>
              <CardHeader>
                  <SectionHeader icon={CreditCard} title="Subscription Details" description="Laden..." />
              </CardHeader>
              <CardContent className="h-40 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </CardContent>
          </Card>
      )
  }

  const isStripeSource = form.watch("source") === BillingSource.STRIPE;
  const isPlanOverridden = form.watch("isPlanOverridden");

  return (
    <>
      <Card>
        <CardHeader>
          <SectionHeader
            icon={CreditCard}
            title="Subscription Details"
            description="Verwalte Abonnement, Plan und Stripe-Verknüpfung"
            rightElement={
              contract ? (
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="bg-red-50 text-red-600 hover:bg-red-100 border border-red-200"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Vertrag löschen
                </Button>
              ) : null
            }
          />
        </CardHeader>
        <CardContent>
          {!contract ? (
             <div className="text-center py-8 text-muted-foreground">
               Kein aktiver Vertrag vorhanden.
             </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="source"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Quelle (Source)</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Quelle wählen" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={BillingSource.STRIPE}>Stripe</SelectItem>
                          <SelectItem value={BillingSource.MANUAL}>Manuell</SelectItem>
                          <SelectItem value={BillingSource.SPECIAL_DEAL}>Special Deal</SelectItem>
                          <SelectItem value={BillingSource.LEGACY_MIGRATION}>Legacy Migration</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                    control={form.control}
                    name="stripeCustomerId"
                    render={({ field }) => (
                        <FormItem>
                        <FormLabel>Stripe Customer ID</FormLabel>
                        <FormControl>
                            <Input {...field} placeholder="cus_..." />
                        </FormControl>
                        <FormMessage />
                        </FormItem>
                    )}
                    />
                    <FormField
                    control={form.control}
                    name="stripeSubscriptionId"
                    render={({ field }) => (
                        <FormItem>
                        <FormLabel>Stripe Subscription ID</FormLabel>
                        <FormControl>
                            <Input {...field} placeholder="sub_..." />
                        </FormControl>
                        <FormMessage />
                        </FormItem>
                    )}
                    />
                    <FormField
                    control={form.control}
                    name="stripePriceId"
                    render={({ field }) => (
                        <FormItem>
                        <FormLabel>Stripe Price ID</FormLabel>
                        <FormControl>
                            <Input {...field} placeholder="price_..." />
                        </FormControl>
                        <FormMessage />
                        </FormItem>
                    )}
                    />
                </div>

                <div className="border-t pt-4 mt-4">
                    <h3 className="text-sm font-medium mb-4">Plan & Status</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                            control={form.control}
                            name="planKey"
                            render={({ field }) => (
                                <FormItem>
                                <FormLabel>Interner Plan</FormLabel>
                                <Select 
                                    onValueChange={field.onChange} 
                                    defaultValue={field.value} 
                                    value={field.value}
                                    disabled={isStripeSource && !isPlanOverridden} 
                                >
                                    <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Plan wählen" />
                                    </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                    <SelectItem value={InternalPlanKey.BASIC}>Basic</SelectItem>
                                    <SelectItem value={InternalPlanKey.PRO}>Pro</SelectItem>
                                    <SelectItem value={InternalPlanKey.CUSTOM}>Custom</SelectItem>
                                    </SelectContent>
                                </Select>
                                {isStripeSource && !isPlanOverridden && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Wird durch Stripe Price ID bestimmt (automatisch).
                                    </p>
                                )}
                                <FormMessage />
                                </FormItem>
                            )}
                            />

                            <FormField
                            control={form.control}
                            name="status"
                            render={({ field }) => (
                                <FormItem>
                                <FormLabel>Status</FormLabel>
                                <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value}>
                                    <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Status wählen" />
                                    </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                    <SelectItem value={BillingStatus.ACTIVE}>Aktiv</SelectItem>
                                    <SelectItem value={BillingStatus.TRIALING}>Trialing</SelectItem>
                                    <SelectItem value={BillingStatus.PAST_DUE}>Past Due</SelectItem>
                                    <SelectItem value={BillingStatus.CANCELED}>Canceled</SelectItem>
                                    <SelectItem value={BillingStatus.INACTIVE}>Inactive</SelectItem>
                                    <SelectItem value={BillingStatus.GRACE_PERIOD}>Grace Period</SelectItem>
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                                </FormItem>
                            )}
                            />
                    </div>
                </div>

                <div className="border rounded-md p-4 bg-muted/20">
                    <FormField
                        control={form.control}
                        name="isPlanOverridden"
                        render={({ field }) => (
                            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                            <FormControl>
                                <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                                <FormLabel>
                                Plan manuell überschreiben
                                </FormLabel>
                                <p className="text-sm text-muted-foreground">
                                Ermöglicht das Setzen eines Plans unabhängig von der Stripe Price ID (z.B. Basic zahlen, Pro nutzen).
                                Verhindert das Überschreiben des Plans durch Stripe Webhooks.
                                </p>
                            </div>
                            </FormItem>
                        )}
                    />

                    {isPlanOverridden && (
                        <FormField
                        control={form.control}
                        name="planOverrideReason"
                        render={({ field }) => (
                            <FormItem className="mt-4">
                            <FormLabel>Grund für Override</FormLabel>
                            <FormControl>
                                <Textarea 
                                    {...field} 
                                    placeholder="Warum weicht der Plan vom Standard ab?" 
                                />
                            </FormControl>
                            <FormMessage />
                            </FormItem>
                        )}
                        />
                    )}
                </div>

                <div className="flex justify-end pt-4">
                  <Button type="submit" disabled={isUpdating}>
                    {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Speichern & Aktualisieren
                  </Button>
                </div>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>

      <StandardDeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDeleteContract}
        isLoading={isDeleting}
        title="Vertrag unwiderruflich löschen"
        description="Bist du dir zu 100% sicher? Diese Aktion löscht den Vertrag aus der Datenbank und kann schwerwiegende Folgen für den Kunden haben."
        itemTitle="Billing Contract"
        itemTypeLabel="ID"
        itemTypeValue={contract?._id}
      >
        <div className="mt-4 p-4 bg-red-50 border border-red-100 rounded-lg space-y-3">
          <div className="flex items-start space-x-2">
            <Checkbox 
              id="cancelStripe" 
              checked={shouldCancelStripe} 
              onCheckedChange={(checked) => setShouldCancelStripe(checked as boolean)}
              className="mt-1 border-red-300 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="cancelStripe"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-red-900"
              >
                Auch in Stripe kündigen/löschen
              </Label>
              <p className="text-xs text-red-600">
                Wenn aktiviert, wird auch die Subscription in Stripe sofort beendet.
                {contract?.stripeSubscriptionId ? ` (ID: ${contract.stripeSubscriptionId})` : " (Keine Stripe ID gefunden)"}
              </p>
            </div>
          </div>
        </div>
      </StandardDeleteDialog>
    </>
  );
};
